/**
 * 常時効果システム: 条件チェックユーティリティ
 *
 * このファイルは常時効果の適用条件をチェックする関数を提供します。
 * 条件オブジェクトを受け取り、対象がその条件を満たすかを判定します。
 *
 * @see src/ルール/continuous-effect-system-design.md
 */

import type { MonsterInstance, CardData } from '../../types/card';
import type { PlayerNumber } from '../../types/player';
import { hasKeyword as hasKeywordAbility } from '../keywordAbilities';

/** 属性制限条件 */
interface MinAttributeOnFieldCondition {
  attribute: string;
  count: number;
}

/** 条件オブジェクト */
export interface Condition {
  // 属性条件
  attribute?: string;
  attributes?: string[];
  notAttribute?: string;
  // カテゴリ条件
  category?: string;
  categories?: string[];
  notCategory?: string;
  // 名前条件
  nameIncludes?: string;
  nameExact?: string;
  nameStartsWith?: string;
  // コスト条件
  maxCost?: number;
  minCost?: number;
  exactCost?: number;
  // 場の状態条件
  minFieldMonsters?: number;
  minAttributeOnField?: MinAttributeOnFieldCondition;
  hasCardOnField?: string;
  hasCategoryOnField?: string;
  hasNameOnField?: string;
  // ライフ条件
  maxLife?: number;
  minLife?: number;
  // ターン条件
  isMyTurn?: boolean;
  isOpponentTurn?: boolean;
  // 除外条件
  excludeSelf?: boolean;
  // カードタイプ条件
  cardType?: 'monster' | 'magic' | 'field' | 'phasecard';
  // 特殊条件
  isFieldCard?: boolean;
  isPhaseCard?: boolean;
  // キーワード条件
  hasKeyword?: string;
  [key: string]: unknown;
}

/** チェック対象（モンスターまたはカード） */
export type ConditionTarget = MonsterInstance | CardData | {
  id?: string;
  name?: string;
  attribute?: string;
  category?: string | string[];
  cost?: number;
  type?: string;
  uniqueId?: string;
  [key: string]: unknown;
};

/** 条件チェック用コンテキスト（必須プロパティ） */
export interface ConditionContext {
  /** 効果のオーナー（必須） */
  effectOwner: PlayerNumber;
  /** 現在のプレイヤー（オプション - ターン条件用） */
  currentPlayer?: PlayerNumber;
  /** P1フィールド（必須） */
  p1Field: (MonsterInstance | null)[];
  /** P2フィールド（必須） */
  p2Field: (MonsterInstance | null)[];
  /** P1ライフ（必須） */
  p1Life: number;
  /** P2ライフ（必須） */
  p2Life: number;
  /** 拡張用 */
  [key: string]: unknown;
}

/**
 * カードが条件を満たすかチェック
 * 全ての条件はANDで結合される（全て満たす必要がある）
 *
 * @param condition - 条件オブジェクト
 * @param target - チェック対象（モンスター/カード）
 * @param context - ゲームコンテキスト
 * @returns 条件を満たす場合true
 */
export const checkCondition = (
  condition: Condition | null | undefined,
  target: ConditionTarget,
  context: ConditionContext
): boolean => {
  // 条件がない場合は常にtrue
  if (!condition) return true;
  if (Object.keys(condition).length === 0) return true;

  // 各条件をチェック（ANDで結合）
  for (const [key, value] of Object.entries(condition)) {
    if (!checkSingleCondition(key, value, target, context)) {
      return false;
    }
  }

  return true;
};

/**
 * 単一の条件をチェック
 *
 * @param conditionKey - 条件のキー
 * @param conditionValue - 条件の値
 * @param target - チェック対象
 * @param context - ゲームコンテキスト
 * @returns 条件を満たすかどうか
 */
const checkSingleCondition = (
  conditionKey: string,
  conditionValue: unknown,
  target: ConditionTarget,
  context: ConditionContext
): boolean => {
  switch (conditionKey) {
    // ========================================
    // 属性条件
    // ========================================

    case 'attribute':
      // 単一属性: 「光属性モンスター」
      return target.attribute === conditionValue;

    case 'attributes':
      // 複数属性（OR）: 「光または闇属性」
      return Array.isArray(conditionValue) && conditionValue.includes(target.attribute);

    case 'notAttribute':
      // 属性除外: 「光属性以外」
      return target.attribute !== conditionValue;

    // ========================================
    // カテゴリ条件
    // ========================================

    case 'category':
      // 単一カテゴリ: 「【ドラゴン】モンスター」
      // conditionValueから【】を除去して比較（cardManager.jsで配列化される際に【】が削除されるため）
      if (!target.category) return false;
      const categoryValue = conditionValue as string;
      const normalizedCategory = categoryValue.replace(/【|】/g, '');
      // target.categoryが配列の場合と文字列の場合の両方に対応
      if (Array.isArray(target.category)) {
        return target.category.includes(normalizedCategory);
      }
      return target.category.includes(categoryValue);

    case 'categories':
      // 複数カテゴリ（OR）: 「【ドラゴン】または【ワイバーン】」
      if (!target.category) return false;
      if (!Array.isArray(conditionValue)) return false;
      // 各条件値から【】を除去して比較
      const categoriesValue = conditionValue as string[];
      const normalizedCategories = categoriesValue.map((cat) => cat.replace(/【|】/g, ''));
      if (Array.isArray(target.category)) {
        return normalizedCategories.some((cat) => target.category!.includes(cat));
      }
      return categoriesValue.some((cat) => target.category!.includes(cat));

    case 'notCategory':
      // カテゴリ除外
      if (!target.category) return true;
      const notCategoryValue = conditionValue as string;
      const normalizedNotCategory = notCategoryValue.replace(/【|】/g, '');
      if (Array.isArray(target.category)) {
        return !target.category.includes(normalizedNotCategory);
      }
      return !target.category.includes(notCategoryValue);

    // ========================================
    // 名前条件
    // ========================================

    case 'nameIncludes':
      // 名前に含む: 「《粘液獣》と名のついた」
      return !!(target.name && target.name.includes(conditionValue as string));

    case 'nameExact':
      // 名前が一致: 「《ブリザードマスター》」
      return target.name === conditionValue;

    case 'nameStartsWith':
      // 名前が〜で始まる
      return !!(target.name && target.name.startsWith(conditionValue as string));

    // ========================================
    // コスト条件
    // ========================================

    case 'maxCost':
      // コスト以下: 「コスト3以下」
      return typeof target.cost === 'number' && target.cost <= (conditionValue as number);

    case 'minCost':
      // コスト以上: 「コスト5以上」
      return typeof target.cost === 'number' && target.cost >= (conditionValue as number);

    case 'exactCost':
      // コストが一致
      return target.cost === conditionValue;

    // ========================================
    // 場の状態条件
    // ========================================

    case 'minFieldMonsters':
      // 場のモンスター数以上
      return countMonstersOnField(context) >= (conditionValue as number);

    case 'minAttributeOnField': {
      // 特定属性の数以上: { attribute: '光', count: 2 }
      const attrCondition = conditionValue as MinAttributeOnFieldCondition;
      return countAttributeOnField(context, attrCondition.attribute) >= attrCondition.count;
    }

    case 'hasCardOnField':
      // 特定カードが場にある
      return hasCardOnField(context, conditionValue as string);

    case 'hasCategoryOnField':
      // 特定カテゴリが場にある
      return hasCategoryOnField(context, conditionValue as string);

    case 'hasNameOnField':
      // 特定名前のカードが場にある
      return hasNameOnField(context, conditionValue as string);

    // ========================================
    // ライフ条件
    // ========================================

    case 'maxLife':
      // ライフ以下: 「ライフが2000以下」
      return getOwnerLife(context) <= (conditionValue as number);

    case 'minLife':
      // ライフ以上: 「ライフが4000以上」
      return getOwnerLife(context) >= (conditionValue as number);

    // ========================================
    // ターン条件
    // ========================================

    case 'isMyTurn':
      // 自分のターン中のみ
      return context.currentPlayer === context.effectOwner;

    case 'isOpponentTurn':
      // 相手のターン中のみ
      return context.currentPlayer !== context.effectOwner;

    // ========================================
    // 除外条件
    // ========================================

    case 'excludeSelf':
      // 自身を除外（カウント時に使用）
      // これは値計算時に使用されるので、ここではtrueを返す
      return true;

    // ========================================
    // カードタイプ条件
    // ========================================

    case 'cardType':
      // カードタイプ: 'monster', 'magic', 'field'
      return target.type === conditionValue;

    // ========================================
    // 特殊条件
    // ========================================

    case 'isFieldCard':
      // フィールドカードかどうか
      return target.type === 'field';

    case 'isPhaseCard':
      // フェイズカードかどうか
      return target.type === 'phasecard';

    // ========================================
    // キーワード条件
    // ========================================

    case 'hasKeyword':
      // キーワード能力を持つかどうか: 「【飛行】を持つモンスター」
      return hasKeywordAbility(target as any, conditionValue as string);

    default:
      // 未知の条件はtrueを返す（警告を出すことも検討）
      console.warn(`Unknown condition key: ${conditionKey}`);
      return true;
  }
};

// ========================================
// ヘルパー関数
// ========================================

/**
 * 場のモンスター数をカウント
 * @param context - ゲームコンテキスト
 * @returns モンスター数
 */
const countMonstersOnField = (context: ConditionContext): number => {
  const { effectOwner, p1Field, p2Field } = context;
  const field = effectOwner === 1 ? p1Field : p2Field;
  return field.filter((m) => m !== null).length;
};

/**
 * 場の特定属性モンスター数をカウント
 * @param context - ゲームコンテキスト
 * @param attribute - 属性
 * @returns モンスター数
 */
const countAttributeOnField = (context: ConditionContext, attribute: string): number => {
  const { effectOwner, p1Field, p2Field } = context;
  const field = effectOwner === 1 ? p1Field : p2Field;
  return field.filter((m) => m && m.attribute === attribute).length;
};

/**
 * 特定カードが場にあるかチェック
 * @param context - ゲームコンテキスト
 * @param cardId - カードID
 * @returns カードがあるかどうか
 */
const hasCardOnField = (context: ConditionContext, cardId: string): boolean => {
  const { effectOwner, p1Field, p2Field } = context;
  const field = effectOwner === 1 ? p1Field : p2Field;
  return field.some((m) => m && m.id === cardId);
};

/**
 * 特定カテゴリのカードが場にあるかチェック
 * @param context - ゲームコンテキスト
 * @param category - カテゴリ
 * @returns カテゴリがあるかどうか
 */
const hasCategoryOnField = (context: ConditionContext, category: string): boolean => {
  const { effectOwner, p1Field, p2Field } = context;
  const field = effectOwner === 1 ? p1Field : p2Field;
  // 【】を除去して比較
  const normalizedCategory = category.replace(/【|】/g, '');
  return field.some((m) => {
    if (!m || !m.category) return false;
    if (Array.isArray(m.category)) {
      return m.category.includes(normalizedCategory);
    }
    return m.category.includes(category);
  });
};

/**
 * 特定名前のカードが場にあるかチェック
 * @param context - ゲームコンテキスト
 * @param name - 名前（部分一致）
 * @returns 名前があるかどうか
 */
const hasNameOnField = (context: ConditionContext, name: string): boolean => {
  const { effectOwner, p1Field, p2Field } = context;
  const field = effectOwner === 1 ? p1Field : p2Field;
  return field.some((m) => m && m.name && m.name.includes(name));
};

/**
 * 効果のオーナーのライフを取得
 * @param context - ゲームコンテキスト
 * @returns ライフ値
 */
const getOwnerLife = (context: ConditionContext): number => {
  const { effectOwner, p1Life, p2Life } = context;
  return effectOwner === 1 ? p1Life : p2Life;
};

// ========================================
// エクスポート用ヘルパー関数
// ========================================

/**
 * 場の特定条件を満たすモンスター数をカウント
 * @param context - ゲームコンテキスト
 * @param condition - 条件オブジェクト
 * @param excludeUniqueId - 除外するカードのuniqueId
 * @returns モンスター数
 */
export const countMonstersMatchingCondition = (
  context: ConditionContext,
  condition: Condition | null | undefined,
  excludeUniqueId: string | null = null
): number => {
  const { effectOwner, p1Field, p2Field } = context;
  const field = effectOwner === 1 ? p1Field : p2Field;

  return field.filter((m) => {
    if (!m) return false;
    if (excludeUniqueId && m.uniqueId === excludeUniqueId) return false;
    return checkCondition(condition, m, context);
  }).length;
};

/**
 * 相手の場で特定条件を満たすモンスター数をカウント
 * @param context - ゲームコンテキスト
 * @param condition - 条件オブジェクト
 * @returns モンスター数
 */
export const countOpponentMonstersMatchingCondition = (
  context: ConditionContext,
  condition: Condition | null | undefined
): number => {
  const { effectOwner, p1Field, p2Field } = context;
  const opponentField = effectOwner === 1 ? p2Field : p1Field;

  return opponentField.filter((m) => {
    if (!m) return false;
    return checkCondition(condition, m, context);
  }).length;
};
